import type { Logger } from 'pino';

import type { Controller } from '~/server/src/interfaces';
import type Turfmaster from '~/server/src/tmmodel';

import { gameLogger } from './logger';

export type DirectionalLogDirection = 'inbound' | 'outbound';
export type DirectionalLogPeer = 'controller';

export interface DirectionalLogIds {
  gameId?: number | string;
  playerId?: number | string;
  tournamentId?: number | string;
  requestId?: number | string;
}

export interface DirectionalLogEntry {
  direction: DirectionalLogDirection;
  peer: DirectionalLogPeer;
  event: string;
  phase?: 'request' | 'response' | 'probe' | 'unexpected' | 'ignored';
  ids?: DirectionalLogIds;
  payloadSummary?: Record<string, unknown>;
}

function safeRecord(value: unknown): Record<string, unknown> | undefined {
  if (!value || typeof value !== 'object') return undefined;
  return value as Record<string, unknown>;
}

function summarizeGame(game: unknown): Record<string, unknown> | undefined {
  const g = safeRecord(game);
  if (!g) return undefined;
  const players = Array.isArray(g.players) ? g.players : undefined;
  const horses = Array.isArray(g.horses) ? g.horses : undefined;
  return {
    gameid: g.gameid,
    name: g.name,
    round: g.round,
    currentState: g.currentState,
    turnpos: g.turnpos,
    turntype: g.turntype,
    playerCount: players?.length,
    horseCount: horses?.length,
  };
}

function summarizeBoard(board: unknown): Record<string, unknown> | undefined {
  const b = safeRecord(board);
  if (!b) return undefined;
  const fields = Array.isArray(b.fields) ? b.fields : undefined;
  return {
    boardid: b.boardid ?? b.boardId,
    lanes: fields?.length,
  };
}

function summarizeParams(params: unknown): Record<string, unknown> | undefined {
  const p = safeRecord(params);
  if (!p) return undefined;
  const keys = Object.keys(p);
  // If params is already small, keep it; otherwise only include a small curated subset.
  if (keys.length <= 8) return p;
  const pick: Record<string, unknown> = {};
  for (const k of [
    'gameid',
    'playerid',
    'tournamentid',
    'horseid',
    'carddice',
    'error',
    'currentState',
  ]) {
    if (k in p) pick[k] = p[k];
  }
  pick._keys = keys.slice(0, 25);
  pick._keyCount = keys.length;
  return pick;
}

export function createDirectionalGameLogger(base: Logger = gameLogger) {
  return {
    inbound: (
      event: string,
      options?: {
        phase?: DirectionalLogEntry['phase'];
        ids?: DirectionalLogIds;
        payloadSummary?: Record<string, unknown>;
      },
      message: string = 'controller event',
    ) => {
      const entry: DirectionalLogEntry = {
        direction: 'inbound',
        peer: 'controller',
        event,
        phase: options?.phase,
        ids: options?.ids,
        payloadSummary: options?.payloadSummary,
      };
      base.debug(entry, message);
    },
    outbound: (
      event: string,
      options?: {
        phase?: DirectionalLogEntry['phase'];
        ids?: DirectionalLogIds;
        payloadSummary?: Record<string, unknown>;
      },
      message: string = 'controller event',
    ) => {
      const entry: DirectionalLogEntry = {
        direction: 'outbound',
        peer: 'controller',
        event,
        phase: options?.phase,
        ids: options?.ids,
        payloadSummary: options?.payloadSummary,
      };
      base.debug(entry, message);
    },
  };
}

export const directionalGameLog = createDirectionalGameLogger(gameLogger);

export function summarizeControllerContext(ctx: {
  controller?: Controller;
  state?: number;
  params?: unknown;
  game?: unknown;
  board?: unknown;
}): Record<string, unknown> {
  const controller = ctx.controller as unknown as Record<string, unknown> | undefined;
  const controllerGameId = controller?.gameid ?? controller?.gameId;

  let stateName: string | undefined;
  try {
    const sm = (
      ctx.controller as unknown as {
        constructor?: { stateMachine?: typeof Turfmaster.stateMachine };
      }
    )?.constructor?.stateMachine;
    // intentionally best-effort; some tests/mocks don't provide it
    stateName = sm ? Object.keys(sm)[ctx.state ?? -1] : undefined;
  } catch {
    stateName = undefined;
  }

  return {
    state: ctx.state,
    stateName,
    controllerGameId,
    params: summarizeParams(ctx.params),
    game: summarizeGame(ctx.game),
    board: summarizeBoard(ctx.board),
  };
}
